import _ from 'lodash';
import {
  sqAggregationBinStore,
  sqClusteringStoreV2,
  sqCompositeSearchStore,
  sqConditionWithPropertiesStore,
  sqDigitalFilterStore,
  sqDisplayToolStore,
  sqFftStore,
  sqInvestigateStore,
  sqIsolationForestStoreV2,
  sqOnnxStore,
  sqOperationalizeStatisticsSummaryStore,
  sqPartialLeastSquaresStoreV2,
  sqPredictionPanelStore,
  sqPrincipalComponentAnalysisStoreV2,
  sqScatterConditionStore,
  sqSelfOrganizingMapsStoreV2,
  sqSignalForecastStore,
  sqStatisticsSummaryStore,
  sqThresholdMetricStore,
  sqToolTemplateStore,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorksheetStore,
  sqWorkstepsStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { getItemForTool } from '@/investigate/investigate.utilities';
import { API_TYPES, DISPLAY_MODE, EDIT_MODE } from '@/main/app.constants';
import { setAutoFocusToolsTab } from '@/main/layout.actions';
import { PLUGIN_CATEGORY } from '@/plugin/pluginHost.constants';
import { setSelectedRegion } from '@/scatterPlot/scatterPlot.actions';
import { isTrustedUser } from '@/services/authorization.service';
import {
  getDefaultMaxCapsuleDuration,
  isTemplatesEnabled,
  seeqVersion,
} from '@/services/systemConfiguration.utilities';
import {
  INVESTIGATE_TOOLS,
  MODEL_NAME_TO_TOOL_ID,
  PREVIEW_TREND_TOOLS,
  TREND_TOOLS,
} from '@/toolSelection/investigate.constants';
import { executeAddOnTool, setDisplayMode } from '@/toolSelection/investigateHelpers.actions';
import { loadDatafile } from '@/tools/importDatafile/importDatafile.actions';
import { setDuplicatingItemId } from '@/tools/itemProperties/propertiesPanel.actions';
import { clearCapsules, loadFormulaAndSetSelection } from '@/tools/manualCondition/capsuleGroup.actions';
import { savePreToolCapsuleSelection } from '@/tools/manualCondition/manualCondition.actions';
import { getTemplateInstanceIdFromProps, mapInstanceInputsToFeatures } from '@/tools/models/templateInstance.utilities';
import { getFormulaItemIdFromParameters, mapOnnxInputParameters } from '@/tools/onnx/onnx.utilities';
import {
  extractRehydrateStateFromModelRehydratePayload,
  reconstructOperationalizeChartSelections,
} from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.utilities';
import { findItemIn, getAllItems, getTrendItemScopedTo, getTrendStores } from '@/trend/trendDataHelper.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { findNextColor } from '@/trendData/itemStoreColor.utilities';
import {
  cancelPreviewSeries,
  removePreviewSeries,
  setTrendItemProps,
  setTrendSelectedRegion,
} from '@/trendData/trend.actions';
import { cancelPreviewCapsules, removePreviewCapsules } from '@/trendData/trendCapsule.actions';
import { setEditModeForCondition } from '@/trendData/trendCondition.actions';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { setEditModeForSeries } from '@/trendData/trendSeries.actions';
import { clearOutstandingDebouncedAsyncFunctions } from '@/utilities/asyncDebounce';
import { applyConfigUpgrade } from '@/utilities/configUpgrader.utilities';
import { update } from '@/utilities/derivedDataTree.utilities';
import { NAME_SEARCH_TYPES } from '@/utilities/formula.constants';
import { getDefaultName } from '@/utilities/formula.utilities';
import { getAssetFromAncestors } from '@/utilities/httpHelpers.utilities';
import { isApiError, isForbidden } from '@/utilities/redaction.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import {
  decorateItemWithProperties,
  getNamePrefix,
  getToolType,
  isStringSeries,
  isUserCreatedType,
} from '@/utilities/utilities';
import { tabsetChangeTab } from '@/worksheet/worksheet.actions';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqSystemApi } from '@seeqdev/api/endpoint/SystemApi';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { SeeqNames } from '@seeqdev/constants';
import type {
  ModelRehydratePayload,
  ToolConfigParams,
  ToolItem,
  ToolRehydratePayload,
} from '@/toolSelection/baseTool.store';
import type { ITEM_TYPES } from '@/trendData/trendData.constants';
import type { FormulaItemOutputV1 } from '@seeqdev/api/model/FormulaItemOutputV1';
import type { PluginOutputV1 } from '@seeqdev/api/model/PluginOutputV1';
import type { ThresholdMetricOutputV1 } from '@seeqdev/api/model/ThresholdMetricOutputV1';

/**
 * Service providing investigate actions
 */

export const DEFAULT_WINDOW_DETAILS = 'toolbar=1,location=0,scrollbars=1,statusbar=1,menubar=1,resizable=1';

let addOnToolWindows: Record<string, Window> = {};

export function updateDerivedDataTree(forceUpdate = false) {
  update()(forceUpdate);
}

/**
 * Sets the active tool.
 *
 * @param {String} toolId - the ID of the tool to display. Must be one of INVESTIGATE_TOOLS.
 * @param {String} [mode] - One of DISPLAY_MODE. If not provided it uses the tool's
 *   defaultDisplayMode, if set, or falls back to DISPLAY_MODE.NEW
 * @param {Boolean} [changeTab] - If true changes to the investigate tab
 */
export function setActiveTool(toolId: string, mode?: string, changeTab = true) {
  if (toolId === TREND_TOOLS.MANUAL_CONDITION) {
    savePreToolCapsuleSelection();
  }

  const tool = _.find(sqInvestigateStore.allTools, ['id', toolId]) as any;
  if (!tool) {
    throw new TypeError(`${toolId} is not a valid tool`);
  }
  if (changeTab) {
    setAutoFocusToolsTab(true);
  }
  setToolErrorMessage('');
  removePreviewCapsules();
  cancelPreviewCapsules();
  removePreviewSeries();
  cancelPreviewSeries();

  clearOutstandingDebouncedAsyncFunctions();
  setEditModeForSeries(undefined);
  setEditModeForCondition(null);
  clearCapsules();
  setDuplicatingItemId('');
  if (!sqWorkbookStore.isTopic && changeTab) {
    // only trigger a tabset change if the tab needs to be actually changed or the layout will be toggling the data
    // panel on and off
    const { activeTabIndex, tabs } = sqWorksheetStore.getTabset('sidebar');
    if (tabs[activeTabIndex] !== 'investigate') {
      tabsetChangeTab('sidebar', 'investigate');
    }
  }

  if (tool.type !== SeeqNames.Types.AddOnTool) {
    flux.dispatch('INVESTIGATE_SET_ACTIVE_TOOL', { tool: toolId });
    setDisplayMode(mode || tool.defaultDisplayMode || DISPLAY_MODE.NEW);
  } else {
    executeAddOnTool(tool);
  }
}

/**
 * Parameterize and sanitize target URL
 */
export function prepareTargetUrl(url: string) {
  const { current } = sqWorkstepsStore.getState();

  const parameterizedUrl = _.chain(url)
    .replace('{workbookId}', sqWorkbenchStore.stateParams.workbookId)
    .replace('{worksheetId}', sqWorkbenchStore.stateParams.worksheetId)
    .replace('{workstepId}', current.id ?? '')
    .replace('{seeqVersion}', seeqVersion()!)
    .value();

  return parameterizedUrl;
}

/**
 * If the named add-on tool window is open and we have a reference to it then set focus to the window. Otherwise
 * open a new named add-on tool window.
 */
export function focusOrOpenNamedWindow(url: string, windowName: string, details: string) {
  if (_.isUndefined(addOnToolWindows[windowName]) || _.get(addOnToolWindows[windowName], 'closed')) {
    const newWindow = window.open(url, windowName, details);
    if (newWindow) {
      addOnToolWindows[windowName] = newWindow;
    }
  } else {
    addOnToolWindows[windowName].focus();
  }
}

/**
 * Sets the text to use to filter the available tools and categories
 *
 * @param {String} filter - The string by which to filter the list of tools
 */
export function setToolFilter(filter: string) {
  flux.dispatch('INVESTIGATE_SET_TOOL_FILTER', { filter });
}

/**
 * Sets the item that is being investigated using one or more of the tools.
 *
 * @param {String|Object} idOrItem - The ID of the item to be investigated or an actual item
 * @param {Object} [props] - Additional properties to associate with the item. Useful for passing along additional
 *   information to the panel that loads the item.
 */
export function setItem(idOrItem: string | number | object, props?: object) {
  const item =
    _.isObject(idOrItem) && !_.isNumber(idOrItem)
      ? idOrItem
      : findItemIn(getTrendStores(), idOrItem as string | number);
  if (!item) {
    clearInvestigatedItem();
  } else {
    flux.dispatch('INVESTIGATE_SET_ITEM', { item, props });
  }
}

/**
 * Sets an item which does not exist in a store as the investigate item.
 *
 * @param {object} item - the item
 */
export function setNonStoreItem(item: object) {
  flux.dispatch('INVESTIGATE_SET_ITEM', { item });
}

/**
 * Clears the item being investigated.
 */
export function clearInvestigatedItem() {
  flux.dispatch('INVESTIGATE_CLEAR_ITEM');
}

/**
 * Sets the search name.
 *
 * @param {String} tool - The name of the tool, one of TREND_TOOLS.
 * @param {String} name - The name for the search.
 */
export function setSearchName(tool: string, name: string | number) {
  flux.dispatch('TOOL_SET_SEARCH_NAME', { type: tool, name }, PUSH_IGNORE);
}

/**
 * Loops through each tool and sets the tool name when the tool type is unknown.
 *
 * @param {String} name - The name for the search.
 * @param {String} name - The id of the calculated item.
 */
export function setNameInAllTools(name: string, itemId: string) {
  INVESTIGATE_TOOLS.forEach(({ id }) =>
    flux.dispatch('TOOL_SET_SEARCH_NAME', { type: id, name, id: itemId }, PUSH_IGNORE),
  );
}

/**
 * Sets the state of the expandable advanced parameters section.
 *
 * @param {String} type - The type of the tool, one of TREND_TOOLS.
 * @param {Boolean} collapsed - True if the advanced section is collapsed.
 */
export function setAdvancedParametersCollapsedState(type: string, collapsed: boolean) {
  flux.dispatch('TOOL_SET_ADVANCED_PARAMETERS_COLLAPSED_STATE', {
    type,
    collapsed,
  });
}

/**
 * Sets the derived data panel open state
 *
 * @param {Boolean} derivedDataPanelOpen - True if open; false for closed.
 */
export function setDerivedDataPanelOpen(derivedDataPanelOpen: boolean) {
  flux.dispatch('INVESTIGATE_SET_DERIVED_DATA_PANEL_OPEN', {
    derivedDataPanelOpen,
  });
  if (derivedDataPanelOpen && _.isNil(sqInvestigateStore.derivedDataTree)) {
    updateDerivedDataTree();
  }
}

/**
 * Load, format, and persist add-on tools array
 */
export function loadAddOnTools() {
  if (!isTrustedUser()) {
    return Promise.resolve();
  }

  return sqSystemApi
    .getAddOnTools()
    .then(({ data }) => {
      const addOnTools = _.chain(data.addOnTools)
        .map((tool) => _.omit(tool, ['href', 'effectivePermissions', 'isArchived']))
        .map((tool) => _.assign(tool, { parentId: TREND_TOOLS.ADDON_TOOLS_GROUP }))
        .filter(
          (tool) => !tool.launchLocation || tool.launchLocation === SeeqNames.API.AddOnToolLaunchLocation.ToolsPane,
        )
        .orderBy(['name'])
        .value();

      flux.dispatch('INVESTIGATE_SET_ADDON_TOOLS', { addOnTools });

      // Set models enabled flag - check both system configuration and user capability
      const modelsEnabledForUser = isTemplatesEnabled();
      flux.dispatch('INVESTIGATE_SET_MODELS_ENABLED_FOR_USER', { modelsEnabledForUser });
    })
    .catch((error) => {
      errorToast({ httpResponseOrError: error });
    });
}

/**
 * Filters the supplied array of plugins to those that have the "ToolPane" category and sets them in the store.
 *
 * @param plugins - an array of plugins
 */
export function setPluginTools(plugins: PluginOutputV1[] = []) {
  _.chain(plugins)
    .filter((plugin) => PLUGIN_CATEGORY.TOOL_PANE === plugin.category)
    .map((plugin) => ({
      ...plugin,
      id: `${plugin.identifier}`,
      parentId: 'addon-tools-group',
    }))
    .map((plugin) => _.pick(plugin, ['id', 'name', 'description', 'icon', 'parentId', 'type']))
    .thru((pluginTools) => {
      flux.dispatch('INVESTIGATE_SET_PLUGIN_TOOLS', { pluginTools });
    })
    .value();
}

/**
 * Sets or adds, if multiple, the item for one of the parameters used in the formula.
 *
 * @param {String} type - The type of the tool, one of TREND_TOOLS.
 * @param {String} name - The name of the parameter
 * @param {Object|undefined} item - The item. Undefined is supported for single item parameters since that is what
 * sq-select-item passes when an item is unselected. Multi-select must use the onRemove() callback along with
 * unsetParameterItem.
 */
export function setParameterItem(type: string, name: string, item?: ToolItem | { id: string } | string | null) {
  flux.dispatch('TOOL_SET_PARAMETER_ITEM', { type, name, item });
}

/**
 * Sets or adds, if multiple, the item for one of the parameters used in the formula.
 *
 * @param {String} type - The type of the tool, one of TREND_TOOLS.
 * @param {String} name - The name of the parameter
 * @param {String[]} itemTypes - The item types that should be added, using ITEM_TYPES
 * @param {String[]} excludedIds - The ids that should not be added
 * @param {boolean} excludeStringSignals - True to exclude string signals
 */
export function setParameterItemFromDetailsPane(
  type: string,
  name: string,
  itemTypes: ITEM_TYPES[],
  excludedIds: string[],
  excludeStringSignals = false,
) {
  _.chain(
    getAllItems({
      excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
      itemTypes,
    }),
  )
    .reject((item) => _.includes(excludedIds, item.id))
    .reject((item) => (excludeStringSignals ? isStringSeries(item) : false))
    .thru((items) => decorate({ items }))
    .forEach((item) => setParameterItem(type, name, item))
    .value();
}

/**
 * Unsets or removes, if multiple, the item for one of the parameters used in the formula. Only useful for
 * parameters that are an array of items and used in conjunction with the multi-select functionality of
 * sq-select-item.
 *
 * @param {String} type - The type of the tool, one of TREND_TOOLS.
 * @param {String} name - The name of the parameter
 * @param {Object} item - The item to unset
 */
export function unsetParameterItem(type: string, name: string, item: object) {
  flux.dispatch('TOOL_UNSET_PARAMETER_ITEM', { type, name, item });
}

/**
 * Sets the maximum duration that a capsule can be; used when creating a new condition.
 *
 * @param {String} type - The type of the tool, one of TREND_TOOLS.
 * @param {Number} value - The number that indicates how long the duration is
 * @param {String} units - The units that the value represents
 */
export function setMaximumDuration(type: string, value: number | null, units: string, valid?: boolean) {
  flux.dispatch('TOOL_SET_MAXIMUM_DURATION', { type, value, units, valid });
}

/**
 * Sets the active tool's error message.
 *
 * @param {String} message - The error message to set
 */
export function setToolErrorMessage(message: string) {
  flux.dispatch('INVESTIGATE_SET_TOOL_ERROR_MESSAGE', { message });
}

/**
 * Closes the active tool, returning the UI to either the Overview or the investigate item view.
 */
export function closeInvestigationTool() {
  clearInvestigatedItem();
  setActiveTool(TREND_TOOLS.OVERVIEW, DISPLAY_MODE.NEW, false);
  setToolErrorMessage('');
}

type EditConfigBase = ToolConfigParams & {
  id?: string;
  name?: string;
  instanceId?: string;
  parameters?: ToolRehydratePayload['parameters'];
  formula?: string;
};

type ToolEditConfig = EditConfigBase;
type ModelEditConfig = EditConfigBase & ModelRehydratePayload;

type EditConfigPayload =
  | { handler: 'TOOL_REHYDRATE_FOR_EDIT'; config: ToolEditConfig }
  | { handler: 'MODEL_TOOL_REHYDRATE_FOR_EDIT'; config: ModelEditConfig };

/**
 * Retrieves the calculation from the API and dispatches all necessary requests to re-populate the edit form for
 * the calculation.
 *
 * @param id - The ID of the calculated item
 * @param [editMode=EDIT_MODE.NORMAL] - Duplicate the calculated item instead of loading it, either to
 *   Formula or the same tool as it already is in
 * @returns resolves once the calculation is retrieved
 */
export async function loadToolForEdit(id: string, editMode = EDIT_MODE.NORMAL): Promise<void> {
  // Each migrated tool store added to these handlers needs a matching routing case in
  // toolSelection/test/investigate.actions.test.ts: migratedToolStoreRehydrateCases for plain tools, or the
  // 'routes rehydration to the correct Zustand store based on model type' cases for model tools.
  const zustandToolRehydrateHandlers: Array<(config: ToolRehydratePayload) => void> = [
    (config) => {
      switch (config.type) {
        case TREND_TOOLS.AGGREGATION_BINS_TABLE:
          sqAggregationBinStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.COMPOSITE_SEARCH:
          sqCompositeSearchStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.DISPLAY:
          sqDisplayToolStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.DIGITAL_FILTER:
          sqDigitalFilterStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.FFT_TABLE:
          sqFftStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.ONNX: {
          const formulaParameters = config.parameters ?? [];
          sqOnnxStore.actions.rehydrateForEdit(
            config,
            getFormulaItemIdFromParameters(formulaParameters),
            mapOnnxInputParameters(
              config.onnxInputParameters as { identifier: string; name?: string }[],
              formulaParameters,
            ),
          );
          break;
        }
        case TREND_TOOLS.STATISTICS_SUMMARY:
          sqStatisticsSummaryStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.THRESHOLD_METRIC:
          sqThresholdMetricStore.actions.rehydrateForEdit(config, getDefaultMaxCapsuleDuration());
          break;
        case TREND_TOOLS.PREDICTION:
          sqPredictionPanelStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.SIGNAL_FORECAST:
          sqSignalForecastStore.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.CONDITION_WITH_PROPERTIES:
          sqConditionWithPropertiesStore.actions.rehydrateForEdit(config);
          break;
      }
    },
  ];
  const zustandModelToolRehydrateHandlers: Array<(config: ModelRehydratePayload) => void> = [
    (config) => {
      switch (config.type) {
        case TREND_TOOLS.MODEL_APPLY:
          sqToolTemplateStore.actions.rehydrateForEdit({
            type: config.type,
            modelData: config.modelData!,
            instanceId: config.instanceId!,
          });
          break;
        case TREND_TOOLS.CLUSTERING_V_2:
          sqClusteringStoreV2.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.ISOLATION_FOREST_V_2:
          sqIsolationForestStoreV2.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY:
          sqOperationalizeStatisticsSummaryStore.actions.rehydrateForEdit(
            extractRehydrateStateFromModelRehydratePayload(config),
          );
          break;
        case TREND_TOOLS.PARTIAL_LEAST_SQUARES_V_2:
          sqPartialLeastSquaresStoreV2.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V_2:
          sqPrincipalComponentAnalysisStoreV2.actions.rehydrateForEdit(config);
          break;
        case TREND_TOOLS.SELF_ORGANIZING_MAPS_V_2:
          sqSelfOrganizingMapsStoreV2.actions.rehydrateForEdit(config);
          break;
      }
    },
  ];

  setAutoFocusToolsTab(true);
  const trendStores = getTrendStores();
  const item = findItemIn(trendStores, id);
  // For signals/conditions created by a Datafile, it is the Datafile that must be edited
  if (_.get(item, 'calculationType') === TREND_TOOLS.IMPORTDATAFILE) {
    loadDatafile(_.get(item, 'assets[0].id', id));
    return;
  }
  const trendStoreItem = item;

  const resolveToolConfig = async (): Promise<EditConfigPayload> => {
    const { data: itemWithProperties } = await sqItemsApi.getItemAndAllProperties({ id });
    setTrendItemProps(itemWithProperties.id, { scopedTo: itemWithProperties.scopedTo }, PUSH_IGNORE);
    if (!isUserCreatedType(itemWithProperties.type)) {
      throw 'Only user-created items can be loaded into a tool for edit.';
    }

    const toolItem = (await getItemForTool(itemWithProperties)).data as ThresholdMetricOutputV1 | FormulaItemOutputV1;
    const rawParameters = getFormulaParameters(toolItem) ?? [];
    const parameters = await Promise.all(
      rawParameters
        .filter((parameter) => !parameter.unbound)
        .map(async (parameter: any) => {
          try {
            const {
              data: { properties },
            } = await sqItemsApi.getItemAndAllProperties({ id: parameter.item.id });
            const swapSourceId = properties
              .find((property) => property.name === SeeqNames.Properties.SwapSourceId)
              ?.value?.toUpperCase();
            const valueUnitOfMeasure = properties.find(
              (property) => property.name === SeeqNames.Properties.ValueUom,
            )?.value;
            const numberFormat = properties.find(
              (property) => property.name === SeeqNames.Properties.NumberFormat,
            )?.value;
            return {
              ...parameter,
              item: {
                ...decorateItemWithProperties(
                  _.omitBy(
                    {
                      ...parameter.item,
                      properties,
                      swapSourceId: swapSourceId ? swapSourceId : undefined,
                      valueUnitOfMeasure,
                      numberFormat,
                    },
                    _.isNil,
                  ),
                ),
                assets: [getAssetFromAncestors(parameter.item.ancestors)],
              },
            };
          } catch (error) {
            if (isApiError(error) && isForbidden(error)) {
              return {
                ...parameter,
                item: {
                  ...parameter.item,
                  redacted: true,
                },
              };
            }

            throw error;
          }
        }),
    );

    const toolItemFormula = (toolItem as FormulaItemOutputV1).formula;
    const itemWithToolProps = { ...itemWithProperties, formula: toolItemFormula, parameters };
    const uiConfig = itemWithProperties.properties.find(
      (property) => property.name === SeeqNames.Properties.UIConfig,
    ) as { value?: string } | undefined;
    const uiConfigValue = editMode !== EDIT_MODE.COPY_TO_FORMULA ? uiConfig?.value : undefined;
    const hasSavedConfig = typeof uiConfigValue === 'string';
    let config = hasSavedConfig ? JSON.parse(uiConfigValue) : { id };
    config.type = editMode !== EDIT_MODE.COPY_TO_FORMULA ? getToolType(itemWithProperties) : TREND_TOOLS.FORMULA;

    // TO-DO CRAB-53147: Remove this special color handling for statistics summary items once the bug for color
    // assignment when editing/duplicating is fixed.
    if (config.type === TREND_TOOLS.STATISTICS_SUMMARY) {
      if (editMode === EDIT_MODE.NORMAL && trendStoreItem?.color) {
        config.color = trendStoreItem.color;
      } else if (editMode !== EDIT_MODE.NORMAL) {
        config.color = findNextColor();
      }
    }

    const instanceId = getTemplateInstanceIdFromProps(itemWithProperties.properties);

    const decoratedItem = decorateItemWithProperties(itemWithToolProps);
    // Set additional properties that are not stored in UIConfig
    _.assign(
      config,
      _.pick(decoratedItem, ['id', 'name', 'scopedTo', 'parameters', 'formula', 'signalMetadata', 'conditionMetadata']),
    );

    // Apply the upgrade steps to the config. A config synthesized above for an item without a saved UIConfig is
    // not a read of saved state, so it stays out of the state-read telemetry.
    config = applyConfigUpgrade(config, config.configVersion, undefined, { recordRead: hasSavedConfig });

    if (config.selectedRegion) {
      setTrendSelectedRegion(config.selectedRegion.min, config.selectedRegion.max);
    } else if (config.type === TREND_TOOLS.SCATTER_CONDITION) {
      const selectedRegion = sqScatterConditionStore.getSelectedRegionFromFormula(config.formula, config.parameters);
      setSelectedRegion(selectedRegion);
    }

    if (config?.formula?.indexOf('.validValues()') > 0) {
      _.assign(config, { useValidValues: true });
    }

    addCustomConfigProperties(config, toolItem);

    let handler: EditConfigPayload['handler'] = 'TOOL_REHYDRATE_FOR_EDIT';
    if (config.type === TREND_TOOLS.MODEL_APPLY) {
      if (!instanceId) {
        throw new Error('Model instance id is missing for the item being edited.');
      }
      const { data: instanceData } = await sqTemplatesApi.getInstance({ instanceId });
      const { data: modelData } = await sqTemplatesApi.getTemplate({ templateId: instanceData.templateContainerId! });
      modelData.inputFeatures = mapInstanceInputsToFeatures(instanceData, modelData.inputFeatures ?? []);
      config.modelData = modelData;
      config.instanceId = instanceId;
      config.instanceData = instanceData;
      // Here if the model instance being loaded has a name that matches a system model, we will set the
      // tool type to the corresponding system model tool.
      // TODO: Once CRAB-56474 is complete, map system model names only when the model is a system model
      config.type = MODEL_NAME_TO_TOOL_ID[modelData.name] || TREND_TOOLS.MODEL_APPLY;
      handler = 'MODEL_TOOL_REHYDRATE_FOR_EDIT';

      // Reconstruct operationalize chart-output/statistic selections here (async) so the synchronous
      // rehydrate handler receives them fully resolved.
      if (config.type === TREND_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY) {
        const { controlChartSelectedOutputs, selectedStatistics } = await reconstructOperationalizeChartSelections(
          modelData,
          instanceData,
        );
        config.controlChartSelectedOutputs = controlChartSelectedOutputs;
        config.selectedStatistics = selectedStatistics;
      }
    }

    return { handler, config };
  };

  const finalizeEditConfig = async (payload: EditConfigPayload): Promise<EditConfigPayload> => {
    const { config } = payload;
    // Copies need name prefix/index to be identified and  will not have an ID yet
    if (!config.name) {
      throw new Error('Tool rehydrate requires a name.');
    }
    const oldName = config.name;
    delete config.id;
    delete config.instanceId;
    delete config.name;

    // Need special handling for statistics summary items since they are formula items
    let filters: string[] = [SeeqNames.API.Flags.ExcludeGloballyScoped];
    let types: string[] = NAME_SEARCH_TYPES;
    if (config.type === TREND_TOOLS.STATISTICS_SUMMARY) {
      filters.push(`Result Type==${SeeqNames.FormulaType.StatsSummary}`);
      types = [SeeqNames.Types.FormulaItem];
    }

    const indexedName = await getDefaultName(getNamePrefix(oldName), getTrendItemScopedTo(id), filters, types);
    config.name = indexedName;

    return payload;
  };

  const applyEditConfig = async ({ handler, config }: EditConfigPayload) => {
    flux.dispatch(handler, config);
    if (handler === 'TOOL_REHYDRATE_FOR_EDIT') {
      zustandToolRehydrateHandlers.forEach((handlerFn) => handlerFn(config));
    } else {
      zustandModelToolRehydrateHandlers.forEach((handlerFn) => handlerFn(config));
    }
    setActiveTool(config.type, DISPLAY_MODE.EDIT);
    // This has to be done after setActiveTool as setActiveTool will clear the item id (as we don't want to
    // accidentally leave this field set and copy properties when not duplicating)
    if (editMode === EDIT_MODE.COPY) {
      setDuplicatingItemId(id);
    }

    // Edit modes (editingIds) are cleared by setActiveTool above, so set them appropriately for the type now
    if (editMode === EDIT_MODE.NORMAL) {
      if (config.type === TREND_TOOLS.COMPOSITE_SEARCH || config.type === TREND_TOOLS.MANUAL_CONDITION) {
        setEditModeForCondition(id);
      } else if (_.includes(PREVIEW_TREND_TOOLS, config.type)) {
        setEditModeForSeries(id);
      }
    }

    // setActiveTool clears capsules from the capsule group store, so this logic must appear after the above
    // call
    if (config.type === TREND_TOOLS.MANUAL_CONDITION) {
      if (!config.formula) {
        throw new Error('Manual condition rehydrate requires a formula.');
      }
      await loadFormulaAndSetSelection({
        formula: config.formula,
      });
    }
  };

  try {
    let payload = await resolveToolConfig();
    if (editMode !== EDIT_MODE.NORMAL) {
      payload = await finalizeEditConfig(payload);
    }

    await applyEditConfig(payload);
  } catch (error) {
    errorToast({ httpResponseOrError: error, displayForbidden: true });
  }
}

/**
 * Allows non-formula-based API types (e.g. THRESHOLD_METRIC) to encode type-specific item properties as standard
 * parameters. Formula based calculated items just return the item's parameters unchanged.
 *
 * @param {Object} item - the item for which parameters should be returned
 * @returns {Array} an array of item parameters
 */
export function getFormulaParameters(item: ThresholdMetricOutputV1 | FormulaItemOutputV1) {
  switch (item.type) {
    case API_TYPES.THRESHOLD_METRIC: {
      const thresholdItem = item as ThresholdMetricOutputV1;
      // The threshold items supplied by the user (not isGenerated) must be returned here so that they end up in
      // originalParameters and therefore are available as options in item parameter dropdown
      return _.chain(thresholdItem.thresholds)
        .reject('isGenerated')
        .map((t) => {
          const level = t.priority?.level ?? 0;
          return {
            item: t.item,
            name: `threshold${level > 0 ? 'P' : 'N'}${Math.abs(level)}`,
            unbound: false,
          };
        })
        .concat(
          _.map(
            ['measuredItem', 'boundingCondition'] as const,
            (key) =>
              thresholdItem[key] && {
                item: thresholdItem[key],
                name: key,
                unbound: false,
              },
          ),
        )
        .compact()
        .value();
    }
    default: {
      const formulaItem = item as FormulaItemOutputV1;
      return formulaItem.parameters;
    }
  }
}

/**
 * Add non-item properties to the config for tools that are not based on a formula.
 *
 * @param {Object} config - the configuration object
 * @param {Object} item - the item for which the tool is being loaded
 */
export function addCustomConfigProperties(
  config: Record<string, unknown>,
  item: ThresholdMetricOutputV1 | FormulaItemOutputV1,
) {
  switch (item.type) {
    case API_TYPES.THRESHOLD_METRIC: {
      const thresholdItem = item as ThresholdMetricOutputV1;
      _.assign(
        config,
        _.pick(thresholdItem, [
          'processType',
          'aggregationFunction',
          'duration',
          'period',
          'neutralColor',
          'boundingConditionMaximumDuration',
          'thresholds',
        ]),
      );
    }
  }
}
